import { License, uploadFileToS3, UploadPurpose } from '@uplift/api';
import { useCreateProviderLicenseCollabAgreementMutation, useStateQuery } from '@uplift/queries';
import { Formik } from 'formik';
import { Box, Typography } from '@mui/material';
import { createCollabAgreementDefaultValues, CreateCollabAgreementForm, CreateCollabAgreementFormValues, createCollabAgreementValidationSchema } from '../forms/CreateCollabAgreementForm';
import { useAlert } from '../../AlertProvider';
import { getDefaultDate } from '../utils';
import { Modal, ModalActions, ModalContent, ModalTitle } from '../../Modal';
import { Button } from '../../Button';

export type UploadCollaborativeAgreementModalProps = {
  onClose: () => void;
  onBack?: () => void;
  license: Pick<License, 'id' | 'userId' | 'stateId'>;
};

export const UploadCollaborativeAgreementModal: React.VFC<UploadCollaborativeAgreementModalProps> = ({
  ...props
}) => {
  const alert = useAlert();
  const { data: state } = useStateQuery(props.license.stateId, { suspense: true });

  const createMutation = useCreateProviderLicenseCollabAgreementMutation(props.license.userId, props.license.id);

  const onSubmit = async (values: CreateCollabAgreementFormValues) => {
    if (!values.collaborationAgreement) {
      return;
    }

    const uploadedReq = await uploadFileToS3({
      providerId: props.license.userId,
      purpose: UploadPurpose.collaborationAgreement,
      file: values.collaborationAgreement
    });

    await createMutation.mutateAsync({
      providerId: props.license.userId,
      licenseId: props.license.id,
      data: {
        fileUploadId: uploadedReq.file_upload.id,
        ...values,
        expirationDate: values.noExpiration ? null : getDefaultDate(values.expirationDate),
      }
    });

    alert.success('Collaborative agreement uploaded');
    props.onClose();
  };

  return (
    <Formik
      initialValues={createCollabAgreementDefaultValues}
      validationSchema={createCollabAgreementValidationSchema}
      onSubmit={onSubmit}
    >
      {formik => (
        <Modal open maxWidth="md" fullWidth showCloseIcon onClose={props.onBack || props.onClose}>
          <ModalTitle onClose={props.onBack || props.onClose}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {props.onBack && <Typography variant="overline" color="secondary">Step 2 of 2</Typography>}
              <Typography fontWeight={500}>{state?.fullName ?? ''} Collaborative Agreement</Typography>
            </Box>
          </ModalTitle>
          <ModalContent dividers>
            <form onSubmit={formik.handleSubmit}>
              <CreateCollabAgreementForm />
            </form>
          </ModalContent>
          <ModalActions>
            <Button variant="outlined" color="secondary" disabled={formik.isSubmitting} onClick={props.onBack || props.onClose}>
              {props.onBack ? 'Back' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={formik.isSubmitting}
              onClick={formik.submitForm}
            >
              Submit
            </Button>
          </ModalActions>
        </Modal>
      )}
    </Formik>
  );
};
